import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { ThemeEvents, DiscountUpdateEvent } from '@theme/events';

/* ===================== Configuration MQH ===================== */
const TIER2_CENTS = 8000; // 80€ - Siwak à la menthe
const TIER3_CENTS = 14900; // 149€ - Qamis au choix

const SIWAK_HANDLE = 'siwak-a-la-menthe-offert';

const QAMIS_HANDLES = [
  'qamis-dubai-collection-24-blanc-offert',
  'qamis-dubai-collection-24-kaki-offert',
  'qamis-dubai-collection-24-bleu-marine-offert',
  'qamis-dubai-gris-offert',
  'qamis-dubai-noir-offert',
  'qamis-omani-kaki-offert',
];

const SIZE_LABELS = {
  '54': '54 (XS)',
  '56': '56 (S)',
  '58': '58 (M)',
  '60': '60 (L)',
};

const DISMISS_KEY = 'mqh_gift_tier3_dismissed';
const OVERLAY_ID = 'mqh-gift-tier3-overlay';

/* =========================================================================
   CONTRÔLEUR DU POPUP
   -------------------------------------------------------------------------
   Simple <div> en position fixe (pas de <dialog> natif). Ouverture/fermeture
   = ajout/retrait d'une classe CSS. Auto-guérison : à chaque appel, on
   s'assure qu'il n'existe qu'une seule copie de l'overlay dans la page et
   qu'elle est bien un enfant direct de <body>, quel que soit l'endroit d'où
   elle a été rendue côté Liquid.
   ========================================================================= */

let popupEls = /** @type {Record<string, HTMLElement>} */ ({});
let popupBound = false;
let productsCache = /** @type {Record<string, any> | null} */ (null);
let selectedHandle = /** @type {string | null} */ (null);
let selectedVariantId = /** @type {number | null} */ (null);
let confirming = false;
let onConfirmAdd = /** @type {((handle: string, variantId: number) => Promise<void>) | null} */ (null);

/**
 * Retrouve (ou relocalise) l'overlay et câble ses événements une seule fois.
 * Appelée à chaque check pour rester robuste même si le rendu Liquid change.
 */
function ensurePopup() {
  const all = document.querySelectorAll('#' + OVERLAY_ID);

  if (all.length === 0) {
    popupEls = {};
    popupBound = false;
    return;
  }

  // S'il existe plusieurs copies (ex: rendue à la fois dans le tiroir et
  // dans theme.liquid), on garde celle actuellement ouverte, sinon la
  // première, et on supprime les autres.
  let keep = /** @type {HTMLElement} */ (all[0]);
  all.forEach((el) => {
    if (el.classList.contains('is-open')) keep = /** @type {HTMLElement} */ (el);
  });
  all.forEach((el) => {
    if (el !== keep) el.remove();
  });

  // On s'assure qu'elle est bien un enfant direct de <body>, pour ne
  // jamais être piégée dans un ancêtre transformé/animé (le tiroir panier).
  if (keep.parentElement !== document.body) {
    document.body.appendChild(keep);
  }

  if (popupBound && popupEls.overlay === keep) return;

  popupEls = {
    overlay: keep,
    products: /** @type {HTMLElement} */ (keep.querySelector('[data-gift-products]')),
    sizes: /** @type {HTMLElement} */ (keep.querySelector('[data-gift-sizes]')),
    confirm: /** @type {HTMLElement} */ (keep.querySelector('[data-gift-confirm]')),
    close: /** @type {HTMLElement} */ (keep.querySelector('[data-gift-close]')),
    decline: /** @type {HTMLElement} */ (keep.querySelector('[data-gift-decline]')),
    error: /** @type {HTMLElement} */ (keep.querySelector('[data-gift-error]')),
  };

  if (
    !popupEls.products ||
    !popupEls.sizes ||
    !popupEls.confirm ||
    !popupEls.close ||
    !popupEls.decline ||
    !popupEls.error
  ) {
    return;
  }

  popupEls.close.addEventListener('click', () => closeGiftDialog(true));
  popupEls.decline.addEventListener('click', () => closeGiftDialog(true));
  popupEls.confirm.addEventListener('click', onConfirmClick);
  popupEls.overlay.addEventListener('click', (event) => {
    if (event.target === popupEls.overlay) closeGiftDialog(true);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isGiftDialogOpen()) closeGiftDialog(true);
  });

  popupBound = true;
}

function isGiftDialogOpen() {
  return !!popupEls.overlay && popupEls.overlay.classList.contains('is-open');
}

/**
 * @param {(handle: string, variantId: number) => Promise<void>} addGiftFn
 */
async function openGiftDialog(addGiftFn) {
  ensurePopup();
  if (!popupEls.overlay || isGiftDialogOpen()) return;

  onConfirmAdd = addGiftFn;

  if (!productsCache) {
    productsCache = {};
    await Promise.all(
      QAMIS_HANDLES.map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (res.ok) {
            // @ts-ignore
            productsCache[handle] = await res.json();
          }
        } catch (error) {
          // ce produit sera simplement absent de la sélection
        }
      })
    );
  }

  renderProductCards();
  popupEls.overlay.classList.add('is-open');
  document.body.style.overflow = 'hidden';
}

/** @param {boolean} markDismissed */
function closeGiftDialog(markDismissed) {
  ensurePopup();
  if (popupEls.overlay) {
    popupEls.overlay.classList.remove('is-open');
  }
  document.body.style.overflow = '';
  if (markDismissed) {
    sessionStorage.setItem(DISMISS_KEY, '1');
  }
}

function renderProductCards() {
  const container = popupEls.products;
  container.innerHTML = '';

  QAMIS_HANDLES.forEach((handle) => {
    const product = productsCache?.[handle];
    if (!product) return;

    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'mqh-gift-card';
    card.dataset.handle = handle;

    const img = document.createElement('img');
    img.className = 'mqh-gift-card__img';
    img.loading = 'lazy';
    img.alt = product.title;
    img.src = withWidth(product.featured_image, 200);

    const label = document.createElement('span');
    label.className = 'mqh-gift-card__label';
    label.textContent = product.title.replace(/\s*-\s*Offert$/i, '');

    card.appendChild(img);
    card.appendChild(label);
    card.addEventListener('click', () => selectProduct(handle));
    container.appendChild(card);
  });

  popupEls.sizes.hidden = true;
  popupEls.sizes.innerHTML = '';
  popupEls.confirm.classList.remove('is-ready');
  popupEls.error.hidden = true;
  selectedHandle = null;
  selectedVariantId = null;
}

/** @param {string} handle */
function selectProduct(handle) {
  selectedHandle = handle;
  selectedVariantId = null;
  popupEls.confirm.classList.remove('is-ready');
  popupEls.error.hidden = true;

  [...popupEls.products.children].forEach((card) => {
    card.classList.toggle('is-selected', /** @type {HTMLElement} */ (card).dataset.handle === handle);
  });

  const product = productsCache?.[handle];
  const sizesContainer = popupEls.sizes;
  sizesContainer.innerHTML = '<p class="mqh-gift-dialog__sizes-label">Taille</p>';
  sizesContainer.hidden = false;

  const row = document.createElement('div');
  row.className = 'mqh-gift-dialog__sizes-row';
  sizesContainer.appendChild(row);

  product.variants.forEach((/** @type {any} */ variant) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mqh-gift-size';
    btn.textContent = SIZE_LABELS[variant.option1] || variant.option1;
    btn.disabled = !variant.available;
    btn.addEventListener('click', () => {
      [...row.children].forEach((b) => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');
      selectedVariantId = variant.id;
      popupEls.confirm.classList.add('is-ready');
      popupEls.error.hidden = true;
    });
    row.appendChild(btn);
  });
}

async function onConfirmClick() {
  if (!selectedHandle || !selectedVariantId) {
    popupEls.error.hidden = false;
    return;
  }
  if (confirming) return;
  confirming = true;

  popupEls.error.hidden = true;
  const originalLabel = popupEls.confirm.textContent;
  popupEls.confirm.textContent = 'Ajout en cours...';

  if (onConfirmAdd) {
    await onConfirmAdd(selectedHandle, selectedVariantId);
  }

  popupEls.confirm.textContent = originalLabel;
  confirming = false;
  sessionStorage.removeItem(DISMISS_KEY);
  closeGiftDialog(false);
}

/**
 * @param {string} url
 * @param {number} width
 */
function withWidth(url, width) {
  if (!url) return url;
  return url.includes('?') ? `${url}&width=${width}` : `${url}?width=${width}`;
}

/* =========================================================================
   COMPOSANT DE SURVEILLANCE DU PANIER
   ========================================================================= */

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} reopenBtn
 * @extends {Component<Refs>}
 */
class MqhGiftThreshold extends Component {
  requiredRefs = ['reopenBtn'];

  #busy = false;
  #hasCheckedOnce = false;
  #debounceTimer = /** @type {ReturnType<typeof setTimeout> | null} */ (null);

  connectedCallback() {
    super.connectedCallback();
    ensurePopup();

    document.addEventListener(ThemeEvents.cartUpdate, this.#scheduleCheck);
    document.addEventListener(DiscountUpdateEvent.eventName, this.#scheduleCheck);
    window.addEventListener('pageshow', this.#scheduleCheck);

    this.refs.reopenBtn.addEventListener('click', () => openGiftDialog(this.#addGiftByHandle));

    this.#scheduleCheck();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#scheduleCheck);
    document.removeEventListener(DiscountUpdateEvent.eventName, this.#scheduleCheck);
    window.removeEventListener('pageshow', this.#scheduleCheck);
  }

  #scheduleCheck = () => {
    if (this.#debounceTimer) clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => this.#check(), 250);
  };

  #sectionId() {
    const section = this.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  }

  /**
   * @param {string} handle
   * @param {number} [variantId]
   */
  #addGiftByHandle = async (handle, variantId) => {
    let id = variantId;
    if (!id) {
      const product = await fetch(`/products/${handle}.js`).then((r) => r.json());
      const available = product.variants.find((/** @type {any} */ v) => v.available);
      id = available ? available.id : product.variants[0]?.id;
    }
    if (!id) return;

    const sectionId = this.#sectionId();
    const config = fetchConfig('json', {
      body: JSON.stringify({
        items: [{ id, quantity: 1 }],
        sections: sectionId ? [sectionId] : [],
      }),
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, config);
      const data = await response.json();
      if (sectionId && data.sections && data.sections[sectionId]) {
        morphSection(sectionId, data.sections[sectionId]);
      }
    } catch (error) {
      // no-op, on retentera au prochain check
    }

    this.#scheduleCheck();
  };

  /**
   * @param {string} key
   * @param {number} quantity
   */
  async #setLineQuantity(key, quantity) {
    const sectionId = this.#sectionId();
    const config = fetchConfig('json', {
      body: JSON.stringify({
        id: key,
        quantity,
        sections: sectionId ? [sectionId] : [],
      }),
    });

    try {
      const response = await fetch(Theme.routes.cart_change_url, config);
      const data = await response.json();
      if (sectionId && data.sections && data.sections[sectionId]) {
        morphSection(sectionId, data.sections[sectionId]);
      }
    } catch (error) {
      // no-op, on retentera au prochain check
    }

    this.#scheduleCheck();
  }

  async #check() {
    if (this.#busy) return;
    this.#busy = true;
    const isFirstCheck = !this.#hasCheckedOnce;
    this.#hasCheckedOnce = true;

    try {
      ensurePopup();

      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const total = cart.total_price;
      const siwakItem = cart.items.find((/** @type {any} */ item) => item.handle === SIWAK_HANDLE);
      const qamisLine = cart.items.find((/** @type {any} */ item) => QAMIS_HANDLES.includes(item.handle));

      // --- Garde-fou absolu : sous 149€, le popup ne doit JAMAIS rester ouvert ---
      if (total < TIER3_CENTS && isGiftDialogOpen()) {
        closeGiftDialog(false);
      }

      // --- Garde-fou : un cadeau ne doit jamais avoir une quantité > 1 ---
      if (siwakItem && siwakItem.quantity > 1) {
        this.#busy = false;
        await this.#setLineQuantity(siwakItem.key, 1);
        return;
      }
      if (qamisLine && qamisLine.quantity > 1) {
        this.#busy = false;
        await this.#setLineQuantity(qamisLine.key, 1);
        return;
      }

      // --- Palier 2 : Siwak à la menthe (80€) ---
      if (total >= TIER2_CENTS && !siwakItem) {
        this.#busy = false;
        await this.#addGiftByHandle(SIWAK_HANDLE);
        return;
      }
      if (total < TIER2_CENTS && siwakItem) {
        this.#busy = false;
        await this.#setLineQuantity(siwakItem.key, 0);
        return;
      }

      // --- Palier 3 : Qamis au choix (149€) ---
      if (total < TIER3_CENTS) {
        if (qamisLine) {
          this.#busy = false;
          await this.#setLineQuantity(qamisLine.key, 0);
          return;
        }
        sessionStorage.removeItem(DISMISS_KEY);
        this.refs.reopenBtn.hidden = true;
      } else {
        if (qamisLine) {
          this.refs.reopenBtn.hidden = true;
        } else {
          this.refs.reopenBtn.hidden = false;
          if (!isFirstCheck && sessionStorage.getItem(DISMISS_KEY) !== '1' && !isGiftDialogOpen()) {
            await openGiftDialog(this.#addGiftByHandle);
          }
        }
      }
    } catch (error) {
      // Échec silencieux : le prochain déclencheur retentera
    } finally {
      this.#busy = false;
    }
  }
}

if (!customElements.get('mqh-gift-threshold')) {
  customElements.define('mqh-gift-threshold', MqhGiftThreshold);
}